// src/components/Bidding/SaleAssets/index.js
import React, { useCallback, useContext, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery } from '@apollo/client';
import styled from 'styled-components/native';

import DataState from 'src/components/DataState';
import Modal from 'src/elements/NativeModal';
import { Text } from 'src/styles';
import * as colors from 'src/styles/colors';
import { dollars } from 'src/utils/stringHelpers';
import AuthContext from 'src/contexts/AuthContext';

import {
  SaleAssetContainer,
  BorderedContainer,
  Title,
  Going,
  GoingText,
  goingColor,
} from './elements';
import {
  BIDDING_SALE_ASSETS,
  BIDDING_SALE_ASSETS_WITH_BIDS,
} from './queries';
import BidGroupChoose from './BidGroupChoose';
import AnnouncementIcon from '../Sales/Announcements/AnnouncementIcon';
import TimedMetaRow from './SaleAssets/TimedMetaRow';

const Container = styled(View)`
  height: 100%;
  flex: 1;
  overflow: auto;
  border: none;
`;

const Thumbnail = styled(View)`
  width: 180px;
  align-items: center;
  text-align: left;
`;

const CurrentThumbnail = styled(View)`
  height: 150px;
  width: 240px;
  align-items: center;
  text-align:left;
`;

const CurrentSaleAssetContainer = styled(SaleAssetContainer)`
  border-top-width: 2px;
  border-top-style: solid;
  border-top-color: ${colors.elRed};
  border-bottom-width: 2px;
  border-bottom-style: solid;
  border-bottom-color: ${colors.elRed};
  background-color: ${colors.elGreen};
`;

const LargeText = styled(Text)`
  font-size: 18px;
`;

const SmallText = styled(Text)``;

const WatchingText = styled(Text)`
  margin-left: 20;
  color: green;
  font-weight: 900;
`;

const StatusText = styled(Text)`
  font-weight: 600;
`;

const NextWatching = styled(View)`
  background-color: white;
  margin-vertical: 6px;
`;

const groupedWith = (saleAsset, saleAssets) => {
  if (!saleAsset.pendingBidGroupId) { return null; }
  return saleAssets.filter(entry => (
    entry.pendingBidGroupId === saleAsset.pendingBidGroupId
  ));
};

const getBorderStyle = (saleAsset, inGroup) => {
  if(inGroup?.length && saleAsset === inGroup[0]) {
    return {border: '2px solid blue', borderBottom: 'none'};
  } else if(inGroup?.length && saleAsset === inGroup[inGroup.length - 1]) {
    return {border: '2px solid blue', borderTop: 'none'};
  } else if(inGroup?.length && inGroup.includes(saleAsset)) {
    return {borderLeft: '2px solid blue', borderRight: '2px solid blue'};
  } else {
    return {border: 'none'};
  }
};

export const CurrentSaleAsset = ({ saleAsset, saleAssets, saleId, saleType }) => {
  const { asset } = saleAsset;
  const inGroup = groupedWith(saleAsset, saleAssets);

  return (
    <CurrentSaleAssetContainer style={getBorderStyle(saleAsset, inGroup)}>
      <Thumbnail>
        {asset.primaryPhotoUrl && (
          <img
            src={asset.primaryPhotoUrl}
            alt={asset.name}
            height="100"
            style={{ maxWidth: '100%', borderRadius: '10px' }}
          />
        )}
      </Thumbnail>
      <View style={{flex: 1}}>
        {goingColor(saleAsset) && (
          <Going style={{ backgroundColor: goingColor(saleAsset) }}>
            <GoingText>{saleAsset.biddingStatus}</GoingText>
          </Going>
        )}
        <View style={{display: 'flex', flexDirection: 'row'}}>
          <Title style={{color: '#fff'}}>{saleAsset.correctedLotNumber}</Title>
          <AnnouncementIcon saleAsset={saleAsset} />
        </View>
        {inGroup && (
          <Title style={{backgroundColor: 'yellow'}}>
            Choice: {inGroup.map(entry => entry.correctedLotNumber).join(' ')}
          </Title>
        )}
        <SmallText style={{color: '#fff'}}>{asset.name}</SmallText>
        <SmallText style={{color: '#fff'}}>{asset.vin}</SmallText>
        <SmallText style={{color: '#fff'}}>
          {asset.cityState?.toUpperCase()}
        </SmallText>

        <TimedMetaRow saleId={saleId} saleType={saleType} saleAsset={saleAsset} />
      </View>
    </CurrentSaleAssetContainer>
  );
};

export const SaleAsset = ({ saleAsset, active, even, saleAssets, saleId, saleType, updateSale }) => {
  const { asset } = saleAsset;
  const { currentUser } = useContext(AuthContext);
  const sold = currentUser?.buyer &&
    saleAsset.activeOffer?.buyer?.id === currentUser?.buyer?.id;
  const pendingSellerConfirmation =
    saleAsset.biddingStatus === "Sold" && saleAsset.pendingSellerConfirmation;

  const inGroup = groupedWith(saleAsset, saleAssets);

  let highlight;
  if (pendingSellerConfirmation) {
    highlight = 'red';
  } else if (sold) {
    highlight = 'green';
  }

  const slug = asset => (
    asset.name.replace(/[^0-9a-z]+/gi, '-')
  );

  const handlePress = () => {
    if (saleType === 'Timed' && typeof updateSale === 'function') {
      updateSale(saleAsset);
    } else {
      window.open(`/sales/${saleId}/inventory/${asset.id}/${slug(asset)}`);
    }
  };

  return (
    <Pressable onPress={handlePress}>
      <SaleAssetContainer
        active={active}
        highlight={highlight}
        groupPosition={saleAsset.groupPosition}
        even={even}
        sold={'Sold' === saleAsset.biddingStatus}
        style={getBorderStyle(saleAsset, inGroup)}
      >
        <Thumbnail>
          {asset.primaryPhotoUrl && (
            <img
              src={asset.primaryPhotoUrl}
              alt={asset.name}
              height="100"
              style={{ maxWidth: '100%', borderRadius: '10px' }}
            />
          )}
        </Thumbnail>
        <View style={{flex: 1}}>
          <View style={{display: 'flex', flexDirection: 'row'}}>
            <SmallText>{saleAsset.correctedLotNumber}</SmallText>
            <AnnouncementIcon saleAsset={saleAsset} />
            {saleAsset.watching && (
              <WatchingText>★ WATCHING ★</WatchingText>
            )}
          </View>

          {goingColor(saleAsset) && (
            <GoingText style={{ color: goingColor(saleAsset) }}>
              {saleAsset.biddingStatus}
            </GoingText>
          )}
          {'Passed' === saleAsset.biddingStatus && (
            <StatusText>PASSED</StatusText>
          )}
          {'Sold' === saleAsset.biddingStatus && (
            <StatusText>
              {pendingSellerConfirmation ? 'Pending Seller Confirmation' : 'SOLD'}
              {' - '}{dollars(saleAsset.biddingSoldAmount)}
            </StatusText>
          )}

          <SmallText>{asset.name}</SmallText>
          <SmallText>{asset.vin}</SmallText>
          <SmallText>{asset.cityState?.toUpperCase()}</SmallText>

          <TimedMetaRow saleId={saleId} saleType={saleType} saleAsset={saleAsset} />
        </View>
      </SaleAssetContainer>
    </Pressable>
  );
};

export const isActive = (sale, saleAsset) => {
  return saleAsset.id === sale.biddingSaleAsset?.id;
};

const Tabs = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const TabContainer = styled(Pressable)`
  flex: 1;
  padding: 6px;
  background-color: ${props => props.active ? colors.elRed : '#fff'};
  borderTopRightRadius: 10px;
  borderTopLeftRadius: 10px;
`;

const TabLabel = styled(Text)`
  color: ${props => props.active ? 'white' : 'black'};
  font-weight: 600;
  text-align: center;
`;

const Tab = ({ name, tab, setTab }) => {
  const isActiveTab = tab === name;
  return (
    <TabContainer active={isActiveTab} onPress={() => setTab(name)}>
      <TabLabel active={isActiveTab}>{name}</TabLabel>
    </TabContainer>
  );
};

const SaleAssets = ({ sale, updateSale }) => {
  const { currentUser, isLoggedIn } = useContext(AuthContext);

  const [tab, setTab] = useState(
    sale.saleType === 'Timed' ? 'Catalog' : 'Live'
  );

  const QUERY = sale.saleType === 'Timed'
    ? BIDDING_SALE_ASSETS_WITH_BIDS
    : BIDDING_SALE_ASSETS;

  const { data } = useQuery(QUERY, {
    variables: { saleId: sale.id }
  });

  const activeRef = useCallback(element => {
    if (element) {
      element.parentNode.scrollTop = element.offsetTop;
    }
  }, []);

  if (!data) { return <DataState.Loading />; }

  const currentIndex = data.saleAssets.findIndex(item => isActive(sale, item));
  let saleAssets = data.saleAssets;

  let nextWatching = false;

  saleAssets.forEach((saleAsset, index) => {
    if (saleAsset.watching && index > (currentIndex || 0)) {
      if (!nextWatching) {
        nextWatching = index - (currentIndex || 0);
      }
    }
  });

  if (sale.saleType !== 'Timed' && tab === 'Live') {
    saleAssets = data.saleAssets.slice(
      Math.max(0, currentIndex - 2),
      currentIndex + 6
    );
  } else if ('My Purchases' === tab) {
    saleAssets = data.saleAssets.filter(item => (
      item.activeOffer?.buyer?.id === currentUser?.buyer.id
    ));
  }

  const groups = {};
  saleAssets.forEach((saleAsset) => {
    if (saleAsset.pendingBidGroupId) {
      if (groups[saleAsset.pendingBidGroupId]) {
        groups[saleAsset.pendingBidGroupId].push(saleAsset.id);
      } else {
        groups[saleAsset.pendingBidGroupId] = [saleAsset.id];
      }
    }
  });

  saleAssets = saleAssets.map(saleAsset => {
    const group = groups[saleAsset.pendingBidGroupId];
    saleAsset = { ...saleAsset };

    if (group) {
      const indexInGroup = group.indexOf(saleAsset.id);
      if (indexInGroup === 0) saleAsset.groupPosition = 'first';
      else if (indexInGroup === group.length - 1) saleAsset.groupPosition = 'last';
      else saleAsset.groupPosition = 'middle';
    }
    return saleAsset;
  });

  return (
    <>
      <Tabs>
        {sale.saleType !== 'Timed' && (
          <Tab name="Live" tab={tab} setTab={setTab} />
        )}
        <Tab name="Catalog" tab={tab} setTab={setTab} />
        {isLoggedIn && currentUser && (
          <Tab name="My Purchases" tab={tab} setTab={setTab} />
        )}
      </Tabs>

      {nextWatching && (
        <NextWatching>
          <WatchingText>Watched Asset {nextWatching} Away</WatchingText>
        </NextWatching>
      )}

      <Container>
        {saleAssets.map((saleAsset, index) => (
          <React.Fragment key={saleAsset.id}>
            {(tab === 'Catalog' || (sale.saleType !== 'Timed' && tab === 'Live')) &&
              isActive(sale, saleAsset) ? (
              <>
                <div ref={tab === 'Live' ? activeRef : null} />
                <CurrentSaleAsset
                  saleAsset={saleAsset}
                  saleAssets={saleAssets}
                  saleId={sale.id}
                  saleType={sale.saleType}
                />
              </>
            ) : (
              <SaleAsset
                saleAsset={saleAsset}
                active={isActive(sale, saleAsset)}
                even={index % 2 === 0}
                saleAssets={saleAssets}
                saleId={sale.id}
                saleType={sale.saleType}
                updateSale={updateSale}
              />
            )}
          </React.Fragment>
        ))}

        {sale.bidGroupChoiceId &&
          sale.bidGroupBuyerId === currentUser?.buyer?.id && (
            <Modal showModal modalType="Choice" setShowModal={false}>
              <BidGroupChoose sale={sale} saleAssets={saleAssets} />
            </Modal>
          )
        }

        <View style={{ height: '80vw', backgroundColor: 'white' }} />
      </Container>
    </>
  );
};

export default SaleAssets;
